import { type FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { difference } from 'lodash-es';

import { type RootState } from '@editor2/reducers/root';
import PageGroupChangesDiff from './PageGroupChangesDiff';
import ChangesDiff from '../common/ChangesDiff';
import { chapterHeader, pageGroupHeader, pageHeader } from '../styles';
import PageChangesDiff from './PageChangesDiff';
import {
	getChapterByPageGroupId,
	getChapterTitle,
	getEntityById,
	getInitialIndex,
	getPageGroupTitle,
	getPageTitle,
	getParentEntityByPageId
} from '../helpers';
import ExpandChangesButton from '../common/ExpandChangesButton';
import DeletedItem from '../common/DeletedItem';
import ChildrenChangesWrapper from '../common/ChildrenChangesWrapper';

interface Props {
	courseId: number;
	chapterChanges: any;
	index: number;
}

const ChapterChangesDiff: FC<Props> = (props) => {
	const { courseId, chapterChanges, index } = props;

	const { id, page_groups, pages, ...attributeChanges } = chapterChanges;

	const {
		chapters,
		page_groups: coursePageGroups,
		initialCourse
	} = useSelector((state: RootState) => state.courses[courseId]);

	const [showChildren, setShowChildren] = useState(true);

	const initialChapter = getEntityById(initialCourse.chapters, id);
	const currentChapter =
		getEntityById(chapters, id) || chapters.entities[attributeChanges.family_id];
	const initialIndex = getInitialIndex(initialCourse.chapters.ids, initialChapter?.family_id);

	if (Object.keys(chapterChanges).length === 1 && index === initialIndex) return <></>;

	const getChangesPageGroupIds = () => {
		return page_groups.map(
			(entity) => entity.family_id || getEntityById(initialCourse.page_groups, entity.id).family_id
		);
	};

	const getChangesPageIds = () => {
		return pages.map(
			(entity) => entity.family_id || getEntityById(initialCourse.pages, entity.id).family_id
		);
	};

	return (
		<>
			<ChangesDiff
				title={getChapterTitle(currentChapter)}
				index={index}
				initialIndex={initialIndex}
				initial={initialChapter}
				attributeChanges={attributeChanges}
				styles={chapterHeader}
			/>

			{(page_groups || pages) && !showChildren && (
				<ExpandChangesButton onClick={() => setShowChildren(true)} />
			)}

			{showChildren && (
				<ChildrenChangesWrapper setShowChildren={setShowChildren}>
					{page_groups &&
						page_groups.map((pageGroupObj, index) => (
							<PageGroupChangesDiff
								courseId={courseId}
								pageGroupChanges={pageGroupObj}
								index={index}
								initChapter={initialChapter}
								key={index}
							/>
						))}

					{page_groups &&
						difference(initialChapter?.page_groups || [], getChangesPageGroupIds()).map(
							(family_id) => {
								const chapter = getChapterByPageGroupId(chapters, family_id);

								return (
									<DeletedItem
										key={family_id}
										title={getPageGroupTitle(initialCourse.page_groups.entities[family_id])}
										movedTo={chapter && getChapterTitle(chapter)}
										styles={pageGroupHeader}
									/>
								);
							}
						)}
				</ChildrenChangesWrapper>
			)}

			{showChildren && (
				<ChildrenChangesWrapper setShowChildren={setShowChildren}>
					{pages &&
						pages.map((pageObj, index) => (
							<PageChangesDiff
								courseId={courseId}
								pageChanges={pageObj}
								index={index}
								initChapter={initialChapter}
								key={index}
							/>
						))}

					{pages &&
						difference(initialChapter?.pages || [], getChangesPageIds()).map((family_id) => {
							const pageGroup = getParentEntityByPageId(coursePageGroups, family_id);
							const chapter = getParentEntityByPageId(chapters, family_id);

							const movedTo =
								(pageGroup && getPageGroupTitle(pageGroup)) ||
								(chapter && getChapterTitle(chapter));

							return (
								<DeletedItem
									key={family_id}
									title={getPageTitle(initialCourse.pages.entities[family_id])}
									movedTo={movedTo}
									styles={pageHeader}
								/>
							);
						})}
				</ChildrenChangesWrapper>
			)}
		</>
	);
};

export default ChapterChangesDiff;
